import { useCallback, useEffect, useRef, useState } from 'react'

import ArrowLeftIcon from '@heroicons/react/outline/ArrowLeftIcon'
import loadable from '@loadable/component'
import { useDecision } from '@optimizely/react-sdk'
import { captureException } from '@sentry/react'

import {
  CustomSeoDetail,
  MutationAddPostArgs,
  MutationUpdatePostArgs,
  Post,
  PostStatus,
  Space,
} from '@tribeplatform/gql-client/types'
import {
  useAddPost,
  useAuthMember,
  usePost,
  useSpace,
  useUpdatePost,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { memberToMentionConverter } from '../../lib/composer.utils'
import { FeaturesList } from '../../lib/optimizely/feature.list'
import { getMappingFields, getPostFieldValue } from '../../utils/post'
import { SelectableTag } from '../Space/SpaceTagsSelector'
import { Attachment } from '../TipTap/@types'
import { useAttachments } from '../TipTap/hooks/useAttachments'
import { useTiptap } from '../TipTap/hooks/useTiptap'
import { composerError } from '../TipTap/utils/errors'
import { PostComposerActiveView } from './@types'
import { ComposerModalContent } from './ComposerModalContent'
import { usePostSeoDetail } from './hooks/usePostSeoDetail'
import { PostModalHeader } from './PostModalHeader'
import { PostSeoDetails } from './PostSeoDetail'
import { PostSpacePicker } from './PostSpacePicker'

const sanitizeSeoDetails = (seoDetail: CustomSeoDetail): CustomSeoDetail => {
  const sanitizedSeoDetail: any = {}
  if (seoDetail.title) {
    sanitizedSeoDetail.title = seoDetail.title
  }
  if (seoDetail.description) {
    sanitizedSeoDetail.description = seoDetail.description
  }
  if (seoDetail.thumbnailId) {
    sanitizedSeoDetail.thumbnailId = seoDetail.thumbnailId
  }

  return sanitizedSeoDetail
}

const TiptapProvider = loadable(() => import('../TipTap/TiptapProvider'), {
  resolveComponent: module => module.TiptapProvider,
  ssr: false,
})

const DISCUSSION_POST_TYPE_SLUG = 'tribe-discussion'

interface PostModalProps {
  post?: Post
  onClose?: () => void
  space?: Space
}

interface PostInnerModalProps extends PostModalProps {
  attachments: Attachment[]
  handleAttachmentDelete?: (attachment: Attachment) => void
}

const PostInnerModal = ({
  space,
  onClose,
  post,
  attachments,
  handleAttachmentDelete,
}: PostInnerModalProps) => {
  const inputRef = useRef<HTMLInputElement>()
  const { data: authMember, isAdmin, isModerator } = useAuthMember()
  const { editor, isTouched, state } = useTiptap()
  const isNew = !post
  const [titleError, setTitleError] = useState('')

  const initialPostTags =
    post?.tags?.map(t => ({
      id: t.id,
      title: t.title,
    })) || []
  const [selectedTags, setSelectedTags] =
    useState<SelectableTag[]>(initialPostTags)

  const initialPostAttachments = post?.attachments || []

  const initialPostTitle =
    getPostFieldValue(post, 'title') ||
    getPostFieldValue(post, 'question') ||
    ''
  const [title, setTitle] = useState<string>(initialPostTitle)
  const [selectedSpace, setSelectedSpace] = useState<Space>(
    space || post?.space,
  )
  const postType =
    selectedSpace?.authMemberProps?.availablePostTypes?.find(
      t => t.slug === DISCUSSION_POST_TYPE_SLUG,
    ) || selectedSpace?.authMemberProps?.availablePostTypes?.[0]
  // trying to fetch the space once more if postType is not defined
  const { data: fullSpace, isFetching: fetchingFullSpace } = useSpace({
    variables: {
      id: selectedSpace?.id,
    },
    useQueryOptions: {
      enabled: isNew && !postType && !!selectedSpace?.id,
    },
  })

  const [decision] = useDecision(FeaturesList.PostSeoDetails)
  const { mutate: addPost, isLoading: addingPost } = useAddPost()
  const { mutate: updatePost, isLoading: updatingPost } = useUpdatePost()
  const { data: fullPost, isFetching: fetchingFullPost } = usePost({
    id: post?.id,
    useQueryOptions: {
      // disable reading from the cache
      placeholderData: undefined,
      enabled: !isNew && !post?.postType,
    },
  })

  const loading =
    addingPost ||
    updatingPost ||
    state.loading ||
    fetchingFullPost ||
    fetchingFullSpace

  const { seoDetail, setSeoDetail } = usePostSeoDetail({
    isNew,
    post: fullPost || post,
  })

  const confirmClose = useCallback(() => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'Closing the composer will delete the post.',
    )

    if (confirmed) {
      onClose?.()
    }
  }, [onClose])

  const close = () => {
    const haveNewAttachment =
      attachments.map(({ id }) => id).join(',') !==
      initialPostAttachments.map(({ id }) => id).join(',')

    const haveNewTag =
      selectedTags.map(({ id }) => id).join(',') !==
      initialPostTags.map(({ id }) => id).join(',')

    const titleChanged = initialPostTitle.trim() !== title.trim()

    if (
      // Composer's content was changed
      isTouched ||
      haveNewAttachment ||
      haveNewTag ||
      titleChanged
    ) {
      confirmClose()
    } else {
      onClose?.()
    }
  }

  const save = useCallback(() => {
    if (loading) return

    const titleErrorMsg = title.trim() ? '' : "Title can't be empty"

    setTitleError(titleErrorMsg)

    // If the title input is empty, don't save
    if (titleErrorMsg) return

    const content = editor.getHTML() || ''

    let postType = post?.postType || fullPost?.postType

    if (isNew) {
      const _space = fullSpace || selectedSpace
      postType =
        _space?.authMemberProps?.availablePostTypes?.find(
          t => t.slug === DISCUSSION_POST_TYPE_SLUG,
        ) || _space?.authMemberProps?.availablePostTypes?.[0]
    }

    if (!postType) {
      captureException('PostType is undefined', {
        tags: {
          isNew,
          post: !!post,
          fullPost: !!fullPost,
          fullSpace: Array.isArray(
            fullSpace?.authMemberProps?.availablePostTypes,
          ),
          spaceTypes: Array.isArray(
            selectedSpace?.authMemberProps?.availablePostTypes,
          ),
        },
      })
    }

    const mappingFields = getMappingFields({
      content,
      title,
      postTypeName: postType?.name,
    })

    const commonInput:
      | MutationAddPostArgs['input']
      | MutationUpdatePostArgs['input'] = {
      mappingFields,
      publish: true,
      attachmentIds: attachments.map(({ id }) => id),
      tagNames: selectedTags.map(({ title }) => title),
    }

    if (seoDetail) {
      commonInput.customSeoDetail = sanitizeSeoDetails(seoDetail)
    }

    if (isNew) {
      addPost(
        {
          input: {
            ...commonInput,
            postTypeId: postType.id,
          },
          spaceId: selectedSpace?.id,
        },
        {
          onSuccess: data => {
            onClose()
            if (data.status === PostStatus.BLOCKED) {
              toast({
                title: 'Pending Review',
                description:
                  'Your post will be visible to others once it’s been reviewed by a moderator.',
                status: 'info',
              })
            }
          },
          onError: ({ message }) => composerError(message),
        },
      )
    } else {
      updatePost(
        {
          id: post.id,
          input: { ...commonInput },
        },
        {
          onSuccess: data => {
            onClose()
            if (data.status === PostStatus.BLOCKED) {
              toast({
                title: 'Pending Review',
                description:
                  'Your post will be visible to others once it’s been reviewed by a moderator.',
                status: 'info',
              })
            }
          },
          onError: ({ message }) => composerError(message),
        },
      )
    }
  }, [
    seoDetail,
    fullSpace,
    fullPost,
    loading,
    addPost,
    attachments,
    editor,
    isNew,
    onClose,
    post,
    selectedTags,
    selectedSpace,
    title,
    updatePost,
  ])

  useEffect(() => {
    if (editor) {
      editor.setOptions({
        editorProps: {
          handleKeyDown: (view, event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
              save()
              return true
            }
            return false
          },
        },
      })
    }
  }, [save, editor])

  const [activeView, setActiveView] = useState<PostComposerActiveView>(
    !space && !post ? 'spacePicker' : 'editor',
  )

  const owner = post?.owner?.member
  const canViewPostSeoDetail =
    activeView === 'editor' &&
    decision.enabled &&
    (isAdmin || isModerator || owner?.id === authMember.id)

  const views = {
    editor: {
      title: isNew ? 'Create post' : 'Update post',
      content: (
        <ComposerModalContent
          ref={inputRef}
          isNew={isNew}
          title={title}
          setTitle={value => {
            setTitleError('')
            setTitle(value)
          }}
          titleError={titleError}
          owner={owner}
          tags={selectedTags}
          setTags={setSelectedTags}
          space={selectedSpace}
          attachments={attachments}
          handleAttachmentDelete={handleAttachmentDelete}
          loading={loading}
          setActiveView={setActiveView}
          save={save}
        />
      ),
    },
    spacePicker: {
      title: (
        <div className="flex space-x-3 items-center">
          {selectedSpace ? (
            <Button
              rounded
              variant="outline"
              leadingIcon={<ArrowLeftIcon />}
              onClick={() => {
                setActiveView('editor')
              }}
            />
          ) : null}
          <span>Pick a space</span>
        </div>
      ),
      content: (
        <Modal.Content>
          <div className="w-full max-w-2xl mx-auto">
            <PostSpacePicker
              ref={inputRef}
              onSelect={space => {
                setSelectedSpace(space)
                setActiveView('editor')
                // on the next render
                setTimeout(() => {
                  inputRef.current?.focus()
                }, 300)
              }}
            />
          </div>
        </Modal.Content>
      ),
    },
    settings: {
      title: (
        <div className="flex space-x-3 items-center">
          {selectedSpace ? (
            <Button
              rounded
              variant="outline"
              leadingIcon={<ArrowLeftIcon />}
              onClick={() => {
                setActiveView('editor')
              }}
            />
          ) : null}
          <span>Post settings</span>
        </div>
      ),
      content: (
        <Modal.Content className="w-full max-w-2xl mx-auto pb-5 overflow-auto">
          <PostSeoDetails seoDetail={seoDetail} setSeoDetail={setSeoDetail} />
        </Modal.Content>
      ),
    },
  }

  return (
    <Modal
      open
      onClose={close}
      initialFocus={inputRef}
      showZenModeBtn={activeView === 'editor'}
      size="2xl"
      className="inline-flex flex-col sm:m-0"
    >
      <PostModalHeader
        title={views[activeView].title}
        onSettingsClick={() => {
          setActiveView('settings')
        }}
        rightContent={
          activeView === 'settings' && (
            <Button
              variant="primary"
              onClick={() => {
                setActiveView('editor')
              }}
            >
              Done
            </Button>
          )
        }
        showSettings={canViewPostSeoDetail}
      />
      {views[activeView].content}
    </Modal>
  )
}
export const PostModal = ({ space, onClose, post }: PostModalProps) => {
  const initialContent: string =
    getPostFieldValue(post, 'content') ||
    getPostFieldValue(post, 'description') ||
    ''

  const initialPostAttachments = post?.attachments || []
  const { attachments, handleAttachmentSelect, handleAttachmentDelete } =
    useAttachments(initialPostAttachments)

  return (
    <TiptapProvider
      content={initialContent}
      embeds={post?.embeds}
      mentions={post?.mentions.map(memberToMentionConverter)}
      handleAttachmentSelect={handleAttachmentSelect}
    >
      <PostInnerModal
        space={space}
        onClose={onClose}
        post={post}
        attachments={attachments}
        handleAttachmentDelete={handleAttachmentDelete}
      />
    </TiptapProvider>
  )
}
